import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  ClipboardList,
  FolderTree,
  Package,
  BarChart3,
  Wallet,
  CreditCard,
  Plug,
  Store,
  LogOut,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { useAuthStore } from '@/store/auth.store';
import { getNewOrdersCount } from '@/services/orders';
import logoMark from '@/images/resdelivery.png';

const nav: Array<{ to: string; label: string; icon: LucideIcon; soon?: boolean }> = [
  { to: '/', label: 'Главная', icon: LayoutDashboard },
  { to: '/orders', label: 'Заявки', icon: ClipboardList },
  { to: '/categories', label: 'Категории', icon: FolderTree },
  { to: '/products', label: 'Товары', icon: Package },
  { to: '/analytics', label: 'Аналитика', icon: BarChart3 },
  { to: '/debts', label: 'Долги', icon: Wallet },
  { to: '/subscription', label: 'Подписка', icon: CreditCard },
  { to: '/integrations', label: 'Интеграции', icon: Plug },
  { to: '/profile', label: 'Профиль', icon: Store },
];

const statusBadge: Record<string, { label: string; cls: string }> = {
  PENDING: { label: 'На модерации', cls: 'bg-amber-50 text-amber-700' },
  ACTIVE: { label: 'Активен', cls: 'bg-emerald-50 text-emerald-700' },
  BLOCKED: { label: 'Заблокирован', cls: 'bg-red-50 text-red-700' },
};

export function Sidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const supplier = useAuthStore((s) => s.supplier);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const badge = supplier ? statusBadge[supplier.status] : undefined;

  // Счётчик новых заявок в «реальном времени» (polling 15 c + при фокусе окна).
  const { data: newOrders } = useQuery({
    queryKey: ['orders', 'new-count'],
    queryFn: getNewOrdersCount,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
  const newCount = newOrders?.count ?? 0;

  return (
    <aside className="h-full w-full bg-white border-r border-line flex flex-col">
      <div className="px-5 py-5 border-b border-line flex items-center gap-3">
        <img src={logoMark} alt="Resstro" className="w-9 h-9 rounded-lg object-cover shrink-0" />
        <div className="leading-tight">
          <div className="font-semibold text-ink">Resstro</div>
          <div className="text-xs text-ink/40">кабинет поставщика</div>
        </div>
      </div>

      {/* Карточка поставщика */}
      <div className="px-3 pt-3">
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-paper border border-line">
          <div className="w-9 h-9 rounded-lg bg-orange/10 text-orange flex items-center justify-center shrink-0 overflow-hidden">
            {supplier?.logoUrl ? (
              <img src={supplier.logoUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <Store size={18} />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-ink/40">Поставщик</div>
            <div className="font-medium text-sm text-ink truncate">{supplier?.name ?? '—'}</div>
          </div>
        </div>
        {badge && (
          <div className="mt-2 px-1">
            <span className={`badge ${badge.cls}`}>{badge.label}</span>
          </div>
        )}
      </div>

      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-auto">
        {nav.map(({ to, label, icon: Icon, soon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            onClick={() => onNavigate?.()}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                isActive ? 'bg-orange-50 text-orange-700 font-medium' : 'text-ink/65 hover:bg-paper'
              }`
            }
          >
            <Icon size={16} />
            <span className="flex-1">{label}</span>
            {to === '/orders' && newCount > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-orange-500 text-white text-[11px] font-bold leading-none tabular-nums">
                {newCount > 99 ? '99+' : newCount}
              </span>
            )}
            {soon && (
              <span className="text-[10px] uppercase tracking-wide text-ink/30 font-normal">
                скоро
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="px-3 py-3 border-t border-line">
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg bg-paper">
          <div className="w-8 h-8 rounded-full bg-ink/10 text-ink flex items-center justify-center text-xs font-bold">
            {(supplier?.name ?? supplier?.email ?? '?').slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-ink truncate">
              {supplier?.name ?? supplier?.email ?? '—'}
            </div>
            <div className="text-xs text-ink/50 truncate">{supplier?.email ?? ''}</div>
          </div>
          <button
            onClick={() => {
              logout();
              navigate('/login');
            }}
            className="p-1.5 rounded hover:bg-white text-ink/50"
            title="Выйти"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
